import { useSeo } from "@/lib/seo";
import { SITE, CONTACT, PEOPLE, waLink } from "@/data/site";
import IMAGES from "@/data/images";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { SSButton } from "@/components/site/SSButton";
import { ContextualCTA } from "@/components/site/ContextualCTA";

export default function About() {
  const suhail = PEOPLE.find((p) => p.key === "suhail");
  const personLd = (p, jobTitle, description, sameAs) => ({
    "@type": "Person",
    name: p.name,
    jobTitle,
    description,
    image: `https://sellingsunsets.co.in${p.photo}`,
    homeLocation: { "@type": "Place", name: "Dehradun, Uttarakhand, India" },
    worksFor: {
      "@type": "ProfessionalService",
      name: "Selling Sunsets",
      url: "https://sellingsunsets.co.in",
      areaServed: "Uttarakhand, India",
    },
    ...(sameAs ? { sameAs } : {}),
  });
  useSeo({
    title: "About",
    description:
      "About Selling Sunsets — a research-led, independent, buyer-focused property-intelligence practice for Uttarakhand, founded by Suhail Abbas Zaidi with co-founder Vibhor Nautiyal in Dehradun.",
    path: "/about",
    image: suhail.photo,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        personLd(
          suhail,
          "Founder",
          "Founder of Selling Sunsets, an independent property-intelligence and buyer-advisory practice for Uttarakhand.",
          ["https://www.linkedin.com/in/suhail-abbas-zaidi"]
        ),
        personLd(
          PEOPLE.find((p) => p.key === "vibhor"),
          "Co-founder",
          "Co-founder of Selling Sunsets, responsible for operations and client delivery across the practice's research and advisory work in Uttarakhand."
        ),
      ],
    },
  });

  return (
    <>
      <PageHeader
        eyebrow="About"
        marker="SS·ABT"
        title="Research-led. Buyer-focused. Independent."
        intro="Selling Sunsets exists to make property decisions in Uttarakhand more intelligent — by replacing sales narrative with research, and listings with understanding."
        breadcrumbs={[{ label: "About" }]}
      />

      <section className="ss-container pb-16 grid lg:grid-cols-[1.3fr_1fr] gap-12 lg:gap-20">
        <div className="space-y-6 max-w-2xl">
          <p className="font-display text-2xl sm:text-3xl font-light leading-snug text-[#1A1C1E]">
            We are not a brokerage, and we do not keep an inventory of listings. We work on the buyer's side.
          </p>
          <p className="text-[1.08rem] leading-[1.8] font-light text-[#3a3f44]">
            Uttarakhand rewards buyers who understand it and penalises those who don't. Terrain, access, land classification and regulation vary enormously across the state — and the difference between a good decision and an expensive one usually lies in what was known before the purchase.
          </p>
          <p className="text-[1.08rem] leading-[1.8] font-light text-[#3a3f44]">
            Our role is to gather that understanding and make it legible: clear location intelligence, honest readings of land and law, and a structured approach to due diligence. Where we do not yet have a verified answer, we say so — and point to the primary source instead of inventing one.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 pt-4">
            {[
              ["Independent", "We represent the buyer, never the seller."],
              ["Evidence-led", "Claims traced to primary sources; nothing fabricated."],
              ["Locally grounded", "Across Garhwal and Kumaon, not a single town."],
              ["Transparent", "Content marked verified, draft or requiring a source."],
            ].map(([t, d]) => (
              <div key={t} className="border-t border-[#E2DDD5] pt-4">
                <p className="ss-overline text-[#8C6A53] mb-1.5">{t}</p>
                <p className="text-[0.95rem] font-light text-[#52575D]">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <Reveal className="ss-hover-grow overflow-hidden aspect-[4/5]">
          <img src={IMAGES.valleyMist.url} alt={IMAGES.valleyMist.alt} className="h-full w-full object-cover" />
        </Reveal>
      </section>

      {/* People behind the research */}
      <section className="bg-[#EFECE6] py-20 lg:py-32" data-testid="people">
        <div className="ss-container">
          <div className="max-w-2xl mb-16 lg:mb-24">
            <p className="ss-overline text-[#8C6A53] mb-5">The people behind the research</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-[#1A1C1E]">
              A real person is behind every assessment
            </h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-[#52575D]">
              Selling Sunsets is deliberately not a personal-brand site. But buyers should know who they are speaking to — people you can reach directly about a decision that matters.
            </p>
          </div>

          <div className="space-y-px">
            {PEOPLE.map((p, i) => (
              <Reveal key={p.key} delay={i * 0.06}>
                <div
                  className="group grid md:grid-cols-[minmax(0,320px)_1fr] gap-8 lg:gap-16 items-center border-t border-[#D9D3C8] py-10 lg:py-14"
                  data-testid={`person-${p.key}`}
                >
                  {/* Portrait */}
                  <div className="relative aspect-[4/5] max-w-[320px] w-full overflow-hidden bg-[#181A1B] ss-hover-grow">
                    {p.photo ? (
                      <img src={p.photo} alt={p.photoAlt || p.name} loading="lazy" className="h-full w-full object-cover object-top" />
                    ) : (
                      <>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="font-display text-[7rem] leading-none text-[#F7F5F0]/15">{p.initials}</span>
                        </div>
                        <div className="absolute inset-0 grain" />
                        <span className="absolute bottom-3 left-3 font-mono-ss text-[0.58rem] tracking-widest text-[#F7F5F0]/45">
                          Portrait · awaiting approved image
                        </span>
                      </>
                    )}
                  </div>

                  {/* Detail */}
                  <div>
                    <p className="ss-overline text-[#8C6A53] mb-3">
                      {p.headline || (p.roleConfirmed && p.role ? p.role : "Role to be confirmed")}
                    </p>
                    <h3 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A1C1E] leading-[0.95]">
                      <Link to={p.key === "suhail" ? "/suhail-abbas-zaidi" : "/vibhor-nautiyal"} className="hover:text-[#8C6A53] transition-colors">{p.name}</Link>
                    </h3>
                    {p.location && (
                      <p className="mt-3 font-mono-ss text-[0.68rem] tracking-widest uppercase text-[#8A9098]">{p.location}</p>
                    )}

                    {p.bio ? (
                      <div className="mt-6 space-y-4 max-w-xl">
                        {p.bio.map((para, j) => (
                          <p key={j} className="text-[1.02rem] leading-[1.8] font-light text-[#3a3f44]">{para}</p>
                        ))}
                      </div>
                    ) : (
                      <>
                        <p className="mt-6 text-[1.02rem] leading-[1.8] font-light text-[#3a3f44] max-w-xl">
                          Part of the team behind Selling Sunsets' research and advisory work across Uttarakhand.
                        </p>
                        <p className="mt-3 text-[0.9rem] font-light text-[#8A9098] italic">
                          Full biography and credentials to be confirmed — we do not publish claims we cannot stand behind.
                        </p>
                      </>
                    )}

                    <div className="mt-7 flex flex-wrap items-center gap-5">
                      <Link to={p.key === "suhail" ? "/suhail-abbas-zaidi" : "/vibhor-nautiyal"} className="ss-link text-[0.8rem] tracking-[0.04em]">Full profile →</Link>
                      {p.key === "suhail" && (
                        <SSButton to="/advisory" variant="primary">Start an advisory conversation</SSButton>
                      )}
                      {p.key === "vibhor" && (
                        <SSButton to="/guided" variant="primary">Begin a guided assessment</SSButton>
                      )}
                      {p.linkedin && (
                        <a
                          href={p.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/li inline-flex items-center gap-2 text-[0.8rem] tracking-[0.04em] text-[#1A1C1E]"
                          data-testid={`person-${p.key}-linkedin`}
                        >
                          <Linkedin className="h-4 w-4 text-[#2D3B32]" strokeWidth={1.5} />
                          <span className="ss-link">LinkedIn</span>
                          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/li:translate-x-0.5 group-hover/li:-translate-y-0.5" strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-[#D9D3C8] pt-8">
            <SSButton
              href={waLink("Hi Suhail, I'd like to speak with you about a property decision in Uttarakhand.")}
              variant="secondary"
              arrow={false}
            >
              WhatsApp us
            </SSButton>
            <p className="text-[0.8rem] font-light text-[#8A9098] max-w-md">
              Credibility through research and direct conversation — not self-congratulatory corporate copy.
            </p>
          </div>
        </div>
      </section>

      <ContextualCTA
        title="Want a second, independent opinion?"
        text="Whether you are still exploring or have shortlisted a property, we can help you think it through clearly."
        whatsappMessage="Hi Suhail, I'd like an independent opinion on a property decision in Uttarakhand."
      />
    </>
  );
}
